import { getUserPreference, settingsServiceApiPaths } from 'features/settings/settingsApi'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { getMinValue } from 'utils/helper'
import { setTntIdInStorage, setTenantIdInStorage } from 'utils/tenantUrlUtils'
import { SELECTED_TENANT_UNAVAILABLE, isManualTenantSwitch, clearTenantSelectionMode } from 'utils/tenantUtils'

export interface ITenant {
    tntId: number
    tenantId: string
    customerId: string
    [key: string]: any
}

/**
 * Gets the user's preferred tenant from backend or falls back to default
 */
export const getPreferredTenant = async (tenants: ITenant[]): Promise<ITenant | null> => {
    if (!tenants || tenants.length === 0) return null

    try {
        const url = settingsServiceApiPaths.userPreferencesGet(UserPreferencesKeysEnum.outboundAiPreferredTenant)
        const preferenceData = await getUserPreference(url)

        if (Object.keys(preferenceData).length > 0) {
            const preferredTenantId = preferenceData.value
            const preferredTenant = tenants.find(t => t.tenantId === preferredTenantId)
            if (preferredTenant) return preferredTenant
        }
    } catch (error) {
        console.log('[TENANT] No user preference found, using default')
    }

    // Fallback to tenant with minimum value
    return getDefaultTenant(tenants)
}

/**
 * Gets the default tenant (minimum value or first available)
 */
export const getDefaultTenant = (tenants: ITenant[]): ITenant | null => {
    if (!tenants || tenants.length === 0) return null

    const tenantOptions = tenants.map(t => ({ value: t.tenantId, ...t }))
    const minTenantValue = getMinValue(tenantOptions)

    return tenants.find(t => t.tenantId === minTenantValue) || tenants[0]
}

/**
 * Initializes tenant in storage with user's preferred tenant
 */
export const initializePreferredTenantInStorage = async (tenants: ITenant[]): Promise<ITenant | null> => {
    const preferredTenant = await getPreferredTenant(tenants)

    if (preferredTenant) {
        setTntIdInStorage(String(preferredTenant.tntId))
        setTenantIdInStorage(preferredTenant.tenantId)
    }

    return preferredTenant
}

/**
 * Finds a tenant by tntId
 */
export const findTenantByTntId = (tenants: ITenant[], tntId: string): ITenant | undefined => {
    return tenants.find(t => String(t.tntId) === tntId)
}

/**
 * Checks if two tenants belong to the same customer
 */
export const isSameCustomer = (tenant1: ITenant, tenant2: ITenant): boolean => {
    return tenant1.customerId === tenant2.customerId
}

/**
 * Handles tenant mismatch between URL and storage
 * Returns redirect URL if redirect is needed, null otherwise
 */
export const handleTenantMismatch = (
    urlTntId: string,
    storageTntId: string,
    tenants: ITenant[],
    pathname: string
): string | null => {
    const urlTenant = findTenantByTntId(tenants, urlTntId)
    const storageTenant = findTenantByTntId(tenants, storageTntId)

    // User doesn't have access to URL tenant
    if (!urlTenant) {
        sessionStorage.setItem(
            SELECTED_TENANT_UNAVAILABLE,
            'The requested Practice is unavailable or you do not have access to it.'
        )
        return `/${storageTntId}/claims`
    }

    // Both tenants valid - check customer relationship
    if (storageTenant && isSameCustomer(urlTenant, storageTenant)) {
        return handleSameCustomerTenantSwitch(urlTenant, storageTntId, pathname)
    }

    // Different customers - show error and redirect
    return handleDifferentCustomerAccess(storageTntId, pathname)
}

/**
 * Handles switching between tenants of the same customer
 */
const handleSameCustomerTenantSwitch = (
    urlTenant: ITenant,
    storageTntId: string,
    pathname: string
): string | null => {
    const isManual = isManualTenantSwitch()

    if (isManual) {
        // Manual switch: sync storage and allow rendering
        setTntIdInStorage(String(urlTenant.tntId))
        setTenantIdInStorage(urlTenant.tenantId)
        clearTenantSelectionMode()
        return null // No redirect needed
    }

    // Automatic switch: redirect to correct tenant
    sessionStorage.setItem(
        SELECTED_TENANT_UNAVAILABLE,
        'Please wait while we switch you to the correct Practice'
    )

    const pathAfterTenant = pathname.substring(pathname.indexOf('/', 1))
    return `/${storageTntId}${pathAfterTenant}`
}

/**
 * Handles access attempt to different customer's tenant
 */
const handleDifferentCustomerAccess = (
    storageTntId: string,
    pathname: string
): string => {
    sessionStorage.setItem(
        SELECTED_TENANT_UNAVAILABLE,
        'The requested Practice is unavailable or you do not have access to it.'
    )

    const pathAfterTenant = pathname.substring(pathname.indexOf('/', 1))
    return `/${storageTntId}${pathAfterTenant}`
}

/**
 * Handles case where URL tenant doesn't match preferred tenant
 */
export const handleUrlTenantMismatch = (
    urlTenant: ITenant,
    preferredTenant: ITenant,
    pathname: string
): string | null => {
    // Same tenant - no action needed
    if (String(urlTenant.tntId) === String(preferredTenant.tntId)) {
        return null
    }

    // Check if both tenants belong to same customer
    if (isSameCustomer(urlTenant, preferredTenant)) {
        sessionStorage.setItem(
            SELECTED_TENANT_UNAVAILABLE,
            'Please wait while we switch you to the correct Practice'
        )
    } else {
        sessionStorage.setItem(
            SELECTED_TENANT_UNAVAILABLE,
            'The requested Practice is unavailable or you do not have access to it.'
        )
    }

    const pathAfterTenant = pathname.substring(pathname.indexOf('/', 1))
    return `/${preferredTenant.tntId}${pathAfterTenant}`
}

/**
 * Handles case where user doesn't have access to URL tenant
 */
export const handleNoTenantAccess = (
    preferredTenant: ITenant
): string => {
    sessionStorage.setItem(
        SELECTED_TENANT_UNAVAILABLE,
        'The requested Practice is unavailable or you do not have access to it.'
    )

    return `/${preferredTenant.tntId}/claims`
}
